import { useEffect, useRef, useState } from "react";

interface RevealOptions {
  threshold?: number | number[];
  rootMargin?: string;
  root?: Element | Document | null;
}

/**
 * Reveals an element once it enters the viewport.
 * Returns a ref to attach and a boolean for the "revealed" state.
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options?: RevealOptions,
) {
  const ref = useRef<T | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px", ...options },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [options]);

  return { ref, revealed };
}

export default useScrollReveal;